import { get } from 'lodash-es';
import { useCallback } from 'react';

import { membersActions } from '@store/members/members.slice';

import { RoleEnum } from '@enum/role.enum';
import { useCurrentApplication } from '@hook/application/use-current-application.hook';
import { useMutation } from '@hook/common/use-mutation.hook';
import { useChangeRoleMutation, useDeleteMemberMutation, useSuspendMemberMutation } from '@hook/memeber/memeber.hooks';
import { MemberInterface } from '@interface/member/member.interface';

import { AccountMemberActionsEnum } from './account-member-actions.enum';

const changeRoleList = new Set([
  AccountMemberActionsEnum.RoleA,
  AccountMemberActionsEnum.RoleD,
  AccountMemberActionsEnum.RoleV,
]);

const roleList = {
  [AccountMemberActionsEnum.RoleA]: RoleEnum.Admin,
  [AccountMemberActionsEnum.RoleD]: RoleEnum.Developer,
  [AccountMemberActionsEnum.RoleV]: RoleEnum.User,
};

export const useAccountMemberActions = (member: MemberInterface) => {
  const { current } = useCurrentApplication();
  const { action: changeRole } = useMutation(useChangeRoleMutation(), membersActions, membersActions.setData);
  const { action: suspendMember } = useMutation(useSuspendMemberMutation(), membersActions, membersActions.setData);
  const { action: deleteMember } = useMutation(useDeleteMemberMutation(), membersActions, membersActions.setData);

  const account = get(current, ['account', 'id']);

  return useCallback(
    ({ key }: { key: AccountMemberActionsEnum }) => {
      if (key === AccountMemberActionsEnum.Suspend) {
        return suspendMember({ account, id: member.id });
      }
      if (key === AccountMemberActionsEnum.Delete) {
        return deleteMember({ account, id: member.id });
      }
      if (changeRoleList.has(key)) {
        return changeRole({
          account,
          id: member.id,
          role: roleList[key],
        });
      }
    },
    [member.id, member.role, member.isSuspended, account],
  );
};
